import { useState, useRef, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import ButtonWithImg from "../buttons/ButtonWithImg";
import CustomDatePicker from "../DatePicker/CustomDatePicker";
import CustomRadioButton from "../buttons/CustomRadioButton";
import Button from "../buttons/Button";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import useTranslation from "../CustomHooks/useTranslation";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import DynamicFilter from "./dynamicFilter";
import { useLocation } from "react-router-dom";

interface FilterProps {
  searchParams?: URLSearchParams;
  onFilter: (filters: { [key: string]: string | boolean | null }) => void;
  disabled: boolean;
  serviceNow?: boolean;
  loading: boolean;
  mainEndPoint?: string;
  mnoList?: boolean;
  amountList?: boolean;
  statusNotFilter?: boolean;
  dynamicFilter?: {
    [key: string]: {
      endDate: any;
      startDate: any;
      mapItem: string[];
      type: string;
      default?: string
    };
  };
  onDynamicFilterChange?: (value: { [key: string]: string | string[] }) => void;
}

interface DynamicFilterValue {
  [key: string]: string | string[] | dayjs.Dayjs | null;
}

const Filter: React.FC<FilterProps> = ({
  onFilter,
  searchParams,
  disabled,
  serviceNow,
  loading,
  mainEndPoint,
  mnoList,
  amountList,
  statusNotFilter,
  dynamicFilter,
}) => {
  const [isFilter, setIsFilter] = useState<boolean>(false);
  let { activeList } = useSelector((state: RootState) => state.activeList);
  const { translate, notEnglish } = useTranslation(breadcrumbsContent);
  const location = useLocation();
  const isServiceFeeList = mainEndPoint?.includes("service-fee");
  const start_date =
    searchParams?.get("start_date") && !mnoList && !amountList
      ? dayjs(searchParams.get("start_date"))
      : mnoList && !amountList && searchParams?.get("contract_start_date")
        ? dayjs(searchParams.get("contract_start_date"))
        : amountList && searchParams?.get("processed_start_date")
          ? dayjs(searchParams.get("processed_start_date"))
          : null;
  const end_date =
    searchParams?.get("end_date") && !mnoList && !amountList
      ? dayjs(searchParams.get("end_date"))
      : mnoList && searchParams?.get("contract_end_date")
        ? dayjs(searchParams.get("contract_end_date"))
        : amountList && searchParams?.get("processed_end_date")
          ? dayjs(searchParams.get("processed_end_date"))
          : null;

  const purchased_start_date =
    searchParams?.get("purchased_start_date") &&
      amountList &&
      !mnoList &&
      activeList === "Purchased"
      ? dayjs(searchParams.get("purchased_start_date"))
      : null;
  const purchased_end_date =
    searchParams?.get("purchased_end_date") &&
      amountList &&
      !mnoList &&
      activeList === "Purchased"
      ? dayjs(searchParams.get("purchased_end_date"))
      : null;

  const processed_start_date =
    searchParams?.get("processed_start_date") && isServiceFeeList
      ? dayjs(searchParams.get("processed_start_date"))
      : null;
  const processed_end_date =
    searchParams?.get("processed_end_date") && isServiceFeeList
      ? dayjs(searchParams.get("processed_end_date"))
      : null;
  const received_start_date =
    searchParams?.get("received_start_date") && isServiceFeeList
      ? dayjs(searchParams.get("received_start_date"))
      : null;
  const received_end_date =
    searchParams?.get("received_end_date") && isServiceFeeList
      ? dayjs(searchParams.get("received_end_date"))
      : null;
  const customer_type =
    useMemo(() => {
      if (searchParams?.get("customer_type") === "Individual") {
        return "Individual";
      } else if (searchParams?.get("customer_type") === "Corporate") {
        return "Corporate";
      } else {
        return "all";
      }
    }, [searchParams]);
  const requestedStartDate = searchParams?.get("requested_start_date") ? dayjs(searchParams.get("requested_start_date")) : null;
  const requestedEndDate = searchParams?.get("requested_end_date") ? dayjs(searchParams.get("requested_end_date")) : null;
  const status = useMemo(() => {
    if (searchParams?.get("status") === "true") return "active";
    if (searchParams?.get("status") === "false") return "inactive";
    return "all";
  }, [searchParams]);
  const service = useMemo(() => {
    if (searchParams?.get("serviceNow") === "true") {
      return "Upfront";
    } else if (searchParams?.get("serviceNow") === "false") {
      return "Prepaid";
    } else {
      return "all";
    }
  }, [searchParams]);
  const creditStatus = useMemo(() => {
    if (searchParams?.get("credit_status") === "true") {
      return "true";
    } else if (searchParams?.get("credit_status") === "false") {
      return "false";
    } else {
      return "all";
    }
  }, [searchParams]);
  const edited_with_compliance = useMemo(() => {
    if (searchParams?.get("edited_with_compliance") === "true") {
      return "true";
    } else if (searchParams?.get("edited_with_compliance") === "false") {
      return "false";
    } else {
      return "all";
    }
  }, [searchParams]);
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(start_date);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(end_date);
  const [purchasedStartDate, setPurchasedStartDate] =
    useState<dayjs.Dayjs | null>(purchased_start_date);
  const [purchasedEndDate, setPurchasedEndDate] = useState<dayjs.Dayjs | null>(
    purchased_end_date
  );

  // Service Fee list date states
  const [processedStartDate, setProcessedStartDate] =
    useState<dayjs.Dayjs | null>(processed_start_date);
  const [processedEndDate, setProcessedEndDate] = useState<dayjs.Dayjs | null>(
    processed_end_date
  );
  const [requestedStartDateValue, setRequestedStartDateValue] = useState<dayjs.Dayjs | null>(requestedStartDate);
  const [requestedEndDateValue, setRequestedEndDateValue] = useState<dayjs.Dayjs | null>(requestedEndDate);

  const [receivedStartDate, setReceivedStartDate] =
    useState<dayjs.Dayjs | null>(received_start_date);
  const [receivedEndDate, setReceivedEndDate] = useState<dayjs.Dayjs | null>(
    received_end_date
  );

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<
    | "date"
    | "status"
    | "serviceNow"
    | "purchased_date"
    | "credit_status"
    | "processed_date"
    | "received_date"
    | "Requested Date Range"
    | "User Role"
    | "Edited with Compliance"
    | "Customer Type"
  >(dynamicFilter && mainEndPoint?.includes('requests') ? "User Role" : "date");
  const [selectedOption, setSelectedOption] = useState(status);
  const [serviceFee, setServiceFee] = useState(service);
  const [customerType, setCustomerType] = useState(customer_type);
  const [creditStatusValue, setCreditStatusValue] = useState(creditStatus);
  const [editedWithCompliance, setEditedWithCompliance] = useState(edited_with_compliance);
  const [dynamicFilterValue, setDynamicFilterValue] =
    useState<DynamicFilterValue>({});
  const filterRef = useRef<HTMLDivElement | null>(null);

  const formatKey = (key: string): string => {
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    // Initialize dynamic filter values from search params
    if (dynamicFilter) {
      const newDynamicFilterValue: DynamicFilterValue = {};
      Object.keys(dynamicFilter).forEach((key) => {
        const paramValue = searchParams?.get(
          key === "Transaction Type"
            ? "type"
            : key.toLowerCase().replace(/\s+/g, "_")
        );
        if (paramValue) {
          if (dynamicFilter[key].type === "checkbox") {
            newDynamicFilterValue[formatKey(key)] = paramValue.split(",");
          } else {
            newDynamicFilterValue[formatKey(key)] = formatKey(paramValue);
          }
        }
      });
      setDynamicFilterValue(newDynamicFilterValue);
    }
  }, [searchParams]);

  const handleClickFilterOpen = () => {
    
    setIsFilter(!isFilter);
    setStartDate(start_date);
    setPurchasedStartDate(purchased_start_date);
    setEndDate(end_date);
    setPurchasedEndDate(purchased_end_date);
    setRequestedStartDateValue(requestedStartDate);
    setRequestedEndDateValue(requestedEndDate);

    // Determine which filter should be active based on applied filters in UI order
    let activeFilterToSet: typeof activeFilter = dynamicFilter && mainEndPoint?.includes('requests') ? "User Role" : "date";

    if (isServiceFeeList) {
      setProcessedStartDate(processed_start_date);
      setProcessedEndDate(processed_end_date);
      setReceivedStartDate(received_start_date);
      setReceivedEndDate(received_end_date);

      // Check filters in UI order for service fee list
      if (processed_start_date || processed_end_date) {
        activeFilterToSet = "processed_date";
      } else if (received_start_date || received_end_date) {
        activeFilterToSet = "received_date";
      } else {
        activeFilterToSet = "processed_date"; // Default for service fee list
      }
    } else {
      // Collect all applied filters to find which one should be active
      const appliedFilters: Array<{ type: typeof activeFilter; priority: number; name: string }> = [];

      // Check each filter type and add to appliedFilters if it has values
      // Priority reflects the UI order - higher number = later in UI = higher priority

      // 1. Date Range (if not requests endpoint) - Priority 1
      if (!mainEndPoint?.includes('requests') && (start_date || end_date)) {
        appliedFilters.push({ type: "date", priority: 1, name: "Date Range" });
      }

      // 2. Purchased Date (for amount list) - Priority 2
      if (amountList && activeList === "Purchased" && (purchased_start_date || purchased_end_date)) {
        appliedFilters.push({ type: "purchased_date", priority: 2, name: "Purchased Date" });
      }

      // 3. Credit Status (for amount list) - Priority 3
      if (amountList && activeList === "Purchased" && creditStatus !== "all") {
        appliedFilters.push({ type: "credit_status", priority: 3, name: "Credit Status" });
      }

      // 4. Status (if not mnoList, amountList, or statusNotFilter) - Priority 4
      if (!mnoList && !amountList && !statusNotFilter && status !== "all") {
        appliedFilters.push({ type: "status", priority: 4, name: "Status" });
      }
      // 5. Service Fee Type - Priority 5
      if (serviceNow && serviceFee !== "all") {
        appliedFilters.push({ type: "serviceNow", priority: 5, name: "Service Fee Type" });
      }

      // 6. Customer Type - Priority 6
      if (mainEndPoint?.includes('customers') && customer_type !== "all") {
        appliedFilters.push({ type: "Customer Type", priority: 6, name: "Customer Type" });
      }

      // 7. Dynamic Filters - Priority 7+
      if (dynamicFilter) {
        let dynamicPriority = 7;
        for (const [key, value] of Object.entries(dynamicFilter)) {
          const paramKey = key === "Transaction Type" ? "type" : key.toLowerCase().replace(/\s+/g, "_");
          const paramValue = searchParams?.get(paramKey);

          // Check for regular dynamic filter values
          if (paramValue && paramValue !== "" && paramValue !== "all") {
            appliedFilters.push({
              type: formatKey(key) as typeof activeFilter,
              priority: dynamicPriority,
              name: `Dynamic: ${key}`
            });
          }
          // Check for date type dynamic filters
          else if (value.type === "date" && (requestedStartDate || requestedEndDate)) {
            appliedFilters.push({
              type: formatKey(key) as typeof activeFilter,
              priority: dynamicPriority,
              name: `Dynamic Date: ${key}`
            });
          }

          dynamicPriority++;
        }
      }

      // 8. Edited with Compliance - Priority 100 (highest)
      if (location?.pathname?.includes('/onboarding-request/modification') && edited_with_compliance !== "all") {
        appliedFilters.push({ type: "Edited with Compliance", priority: 100, name: "Edited with Compliance" });
      }

      // Debug: Log applied filters
      console.log("Applied filters:", appliedFilters);

      // If we have applied filters, select the one with highest priority (last in UI order)
      if (appliedFilters.length > 0) {
        // Sort by priority and take the last one (highest priority)
        appliedFilters.sort((a, b) => a.priority - b.priority);
        const selectedFilter = appliedFilters[0];
        console.log("Selected filter:", selectedFilter);
        activeFilterToSet = selectedFilter.type;
      }
      // If no filter is applied, use default
      else {
        console.log("No filters applied, using default");
        activeFilterToSet = dynamicFilter && mainEndPoint?.includes('requests') ? "User Role" : "date";
      }
    }

    setActiveFilter(activeFilterToSet);
    setCreditStatusValue(creditStatus);
    setEditedWithCompliance(edited_with_compliance);
    setSelectedOption(status);
    setErrorMessage(null);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      filterRef.current &&
      !filterRef.current.contains(event.target as Node)
    ) {
      setIsFilter(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDateChange = (date: dayjs.Dayjs | null, type: string) => {
    if (type === "start") {
      setStartDate(date);
      if (date && endDate && date.isAfter(endDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "end") {
      setEndDate(date);
      if (startDate && date && date.isBefore(startDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "purchased_start") {
      setPurchasedStartDate(date);
      if (date && purchasedEndDate && date.isAfter(purchasedEndDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "purchased_end") {
      setPurchasedEndDate(date);
      if (purchasedStartDate && date && date.isBefore(purchasedStartDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "processed_start") {
      setProcessedStartDate(date);
      if (date && processedEndDate && date.isAfter(processedEndDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "processed_end") {
      setProcessedEndDate(date);
      if (processedStartDate && date && date.isBefore(processedStartDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "received_start") {
      setReceivedStartDate(date);
      if (date && receivedEndDate && date.isAfter(receivedEndDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "received_end") {
      setReceivedEndDate(date);
      if (receivedStartDate && date && date.isBefore(receivedStartDate)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "requested_start") {
      setRequestedStartDateValue(date);
      if (date && requestedEndDateValue && date.isAfter(requestedEndDateValue)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    } else if (type === "requested_end") {
      setRequestedEndDateValue(date);
      if (requestedStartDateValue && date && date.isBefore(requestedStartDateValue)) {
        setErrorMessage("Start date cannot be greater than end date.");
      } else {
        setErrorMessage(null);
      }
    }
  };

  const preventCloseOnDatePickerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleChange = (value: string) => {
    setSelectedOption(value);
  };
  const handleServiceFee = (value: string) => {
    setServiceFee(value);
  };
  const handleCustomerType = (value: string) => {
    setCustomerType(value);
  };
  const handleCreditStatus = (value: string) => {
    setCreditStatusValue(value);
  };
  const handleEditedWithCompliance = (value: string) => {
    setEditedWithCompliance(value);
  };
  const handleApply = () => {
    if (activeFilter === "date") {
      if (!startDate && !endDate) {
        setErrorMessage("This field is required");
        return;
      }
      if (startDate && !endDate) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!startDate && endDate) {
        setErrorMessage("Please select start date");
        return;
      }
      if (startDate && endDate && startDate.isAfter(endDate)) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    } else if (activeFilter === "purchased_date") {
      if (!purchasedStartDate && !purchasedEndDate) {
        setErrorMessage("This field is required");
        return;
      }
      if (purchasedStartDate && !purchasedEndDate) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!purchasedStartDate && purchasedEndDate) {
        setErrorMessage("Please select start date");
        return;
      }
      if (
        purchasedStartDate &&
        purchasedEndDate &&
        purchasedStartDate.isAfter(purchasedEndDate)
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    } else if (activeFilter === "processed_date") {
      if (!processedStartDate && !processedEndDate) {
        setErrorMessage("This field is required");
        return;
      }
      if (processedStartDate && !processedEndDate) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!processedStartDate && processedEndDate) {
        setErrorMessage("Please select start date");
        return;
      }
      if (
        processedStartDate &&
        processedEndDate &&
        processedStartDate.isAfter(processedEndDate)
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    } else if (activeFilter === "received_date") {
      if (!receivedStartDate && !receivedEndDate) {
        setErrorMessage("This field is required");
        return;
      }
      if (receivedStartDate && !receivedEndDate) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!receivedStartDate && receivedEndDate) {
        setErrorMessage("Please select start date");
        return;
      }
      if (
        receivedStartDate &&
        receivedEndDate &&
        receivedStartDate.isAfter(receivedEndDate)
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    } else if (
      activeFilter === "status" ||
      activeFilter === "serviceNow" ||
      activeFilter === "credit_status" ||
      activeFilter === "Edited with Compliance" ||
      activeFilter === "Customer Type"
    ) {
      setErrorMessage(null);
    } else if (activeFilter === "Requested Date Range") {
      if (!requestedStartDateValue && !requestedEndDateValue) {
        setErrorMessage("This field is required");
        return;
      }
      if (requestedStartDateValue && !requestedEndDateValue) {
        setErrorMessage("Please select end date");
        return;
      }
      if (!requestedStartDateValue && requestedEndDateValue) {
        setErrorMessage("Please select start date");
        return;
      }
      if (requestedStartDateValue && requestedEndDateValue && requestedStartDateValue.isAfter(requestedEndDateValue)) {
        setErrorMessage("Start date cannot be greater than end date");
        return;
      }
      setErrorMessage(null);
    }
    const appliedFilters: { [key: string]: any } = {};
    if (selectedOption) {
      appliedFilters["status"] =
        selectedOption === "all"
          ? ""
          : selectedOption === "active"
            ? true
            : false;
    }
    if (serviceFee) {
      appliedFilters["service_fee_type"] =
        serviceFee === "all" ? "" : serviceFee;
    }
    if (customerType) {
      appliedFilters["customer_type"] = customerType === "all" ? "" : customerType;
    }
    if (creditStatusValue) {
      appliedFilters["credit_status"] =
        creditStatusValue === "all" ? "" : creditStatusValue;
    }
    if (editedWithCompliance) {
      appliedFilters["edited_with_compliance"] = editedWithCompliance === "all" ? "" : editedWithCompliance;
    }
    if (isServiceFeeList) {
      appliedFilters["processed_start_date"] = processedStartDate
        ? processedStartDate.format("YYYY-MM-DD")
        : "";
      appliedFilters["processed_end_date"] = processedEndDate
        ? processedEndDate.format("YYYY-MM-DD")
        : "";
      appliedFilters["received_start_date"] = receivedStartDate
        ? receivedStartDate.format("YYYY-MM-DD")
        : "";
      appliedFilters["received_end_date"] = receivedEndDate
        ? receivedEndDate.format("YYYY-MM-DD")
        : "";
    } else if (startDate && !mnoList && !amountList) {
      appliedFilters["start_date"] = startDate.format("YYYY-MM-DD");
    } else if (startDate && mnoList && !amountList) {
      appliedFilters["contract_start_date"] = startDate.format("YYYY-MM-DD");
    } else if (startDate && !purchasedStartDate && amountList && !mnoList) {
      appliedFilters["processed_start_date"] = startDate.format("YYYY-MM-DD");
      appliedFilters["purchased_start_date"] = "";
    } else if (purchasedStartDate && !startDate && amountList && !mnoList) {
      appliedFilters["purchased_start_date"] =
        purchasedStartDate.format("YYYY-MM-DD");
      appliedFilters["processed_start_date"] = "";
    } else if (startDate && purchasedStartDate && amountList) {
      appliedFilters["processed_start_date"] = startDate.format("YYYY-MM-DD");
      appliedFilters["purchased_start_date"] =
        purchasedStartDate.format("YYYY-MM-DD");
    } else if (amountList) {
      appliedFilters["purchased_start_date"] = "";
      appliedFilters["processed_start_date"] = "";
    } else if (mnoList) {
      appliedFilters["contract_start_date"] = "";
    } else {
      appliedFilters["start_date"] = "";
    }
    if (endDate && !mnoList && !amountList) {
      appliedFilters["end_date"] = endDate.format("YYYY-MM-DD");
    } else if (endDate && mnoList && !amountList) {
      appliedFilters["contract_end_date"] = endDate.format("YYYY-MM-DD");
    } else if (endDate && !purchasedEndDate && amountList && !mnoList) {
      appliedFilters["processed_end_date"] = endDate.format("YYYY-MM-DD");
      appliedFilters["purchased_end_date"] = "";
    } else if (purchasedEndDate && !endDate && amountList && !mnoList) {
      appliedFilters["purchased_end_date"] =
        purchasedEndDate.format("YYYY-MM-DD");
      appliedFilters["processed_end_date"] = "";
    } else if (endDate && purchasedEndDate && amountList) {
      appliedFilters["processed_end_date"] = endDate.format("YYYY-MM-DD");
      appliedFilters["purchased_end_date"] =
        purchasedEndDate.format("YYYY-MM-DD");
    } else if (amountList) {
      appliedFilters["purchased_end_date"] = "";
      appliedFilters["processed_end_date"] = "";
    } else if (mnoList) {
      appliedFilters["contract_end_date"] = "";
    } else {
      appliedFilters["end_date"] = "";
    }
    if (dynamicFilter) {
      Object.entries(dynamicFilter).forEach(([key, value]) => {
        const formattedKey =
          key === "Transaction Type"
            ? "type"
            : key.toLowerCase().replace(/\s+/g, "_");
        if (value.type === "date") {
          appliedFilters[value?.startDate] = requestedStartDateValue?.format("YYYY-MM-DD");
          appliedFilters[value?.endDate] = requestedEndDateValue?.format("YYYY-MM-DD");
        }
        else if (Array.isArray(dynamicFilterValue[key])) {
          if (dynamicFilterValue[key].length > 0) {
            appliedFilters[formattedKey] = dynamicFilterValue[key].join(",");
          }
        } else {
          if ((key === "Transaction Type" || key === "Edited with Compliance") && dynamicFilterValue[key] === "all") {
            appliedFilters[formattedKey] = "";
          } else if (key === "Edited with Compliance") {
            appliedFilters[formattedKey] = dynamicFilterValue[key] === "yes" ? true : false;
          } else {
            appliedFilters[formattedKey] =
              dynamicFilterValue[key];
          }
        }
      });
    }
    onFilter(appliedFilters);
    setIsFilter(false);
  };

  const handleClearAll = () => {
    setStartDate(null);
    setEndDate(null);
    setPurchasedStartDate(null);
    setPurchasedEndDate(null);
    setProcessedStartDate(null);
    setProcessedEndDate(null);
    setReceivedStartDate(null);
    setReceivedEndDate(null);
    setRequestedStartDateValue(null);
    setRequestedEndDateValue(null);
    setErrorMessage(null);
    setSelectedOption("all");
    setCustomerType("all");
    setServiceFee("all");
    setCreditStatusValue("all");
    setEditedWithCompliance("all");
    setActiveFilter(dynamicFilter ? "User Role" : "date");
    setDynamicFilterValue({});
    onFilter({
      status: "",
      user_role: "",
      type: "",
      end_date: "",
      start_date: "",
      service_fee_type: "",
      contract_start_date: "",
      contract_end_date: "",
      purchased_start_date: "",
      purchased_end_date: "",
      processed_start_date: "",
      processed_end_date: "",
      credit_status: "",
      edited_with_compliance: "",
      requested_start_date: "",
      requested_end_date: "",
      profile_status: "",
      customer_type: "",
    });
    setIsFilter(false);
  };
  const ClearButtonDisable =
    !errorMessage &&
    startDate === null &&
    endDate === null &&
    purchasedStartDate === null &&
    purchasedEndDate === null &&
    processedStartDate === null &&
    processedEndDate === null &&
    receivedStartDate === null &&
    receivedEndDate === null &&
    requestedStartDateValue === null &&
    requestedEndDateValue === null &&
    selectedOption === "all" &&
    serviceFee === "all" &&
    creditStatusValue === "all" &&
    editedWithCompliance === "all" &&
    customerType === "all" &&
    Object.values(dynamicFilterValue).length === 0;

  const handleFilterActive = (type: any) => {
    setActiveFilter(type);

    // Reset error messages when changing filter types
    setErrorMessage(null);

    // Reset invalid date combinations
    if (
      type !== "date" &&
      ((!startDate && !endDate) ||
        (startDate && !endDate) ||
        (!startDate && endDate) ||
        (startDate && endDate && startDate.isAfter(endDate)))
    ) {
      setStartDate(null);
      setEndDate(null);
    }
    if (
      type !== "purchased_date" &&
      ((!purchasedStartDate && !purchasedEndDate) ||
        (purchasedStartDate && !purchasedEndDate) ||
        (!purchasedStartDate && purchasedEndDate) ||
        (purchasedStartDate &&
          purchasedEndDate &&
          purchasedStartDate.isAfter(purchasedEndDate)))
    ) {
      setPurchasedStartDate(null);
      setPurchasedEndDate(null);
    }
    if (
      type !== "processed_date" &&
      ((!processedStartDate && !processedEndDate) ||
        (processedStartDate && !processedEndDate) ||
        (!processedStartDate && processedEndDate) ||
        (processedStartDate &&
          processedEndDate &&
          processedStartDate.isAfter(processedEndDate)))
    ) {
      setProcessedStartDate(null);
      setProcessedEndDate(null);
    }
    if (
      type !== "received_date" &&
      ((!receivedStartDate && !receivedEndDate) ||
        (receivedStartDate && !receivedEndDate) ||
        (!receivedStartDate && receivedEndDate) ||
        (receivedStartDate &&
          receivedEndDate &&
          receivedStartDate.isAfter(receivedEndDate)))
    ) {
      setReceivedStartDate(null);
      setReceivedEndDate(null);
    }
    if (
      type !== "Requested Date Range" &&
      ((!requestedStartDateValue && !requestedEndDateValue) ||
        (requestedStartDateValue && !requestedEndDateValue) ||
        (!requestedStartDateValue && requestedEndDateValue) ||
        (requestedStartDateValue &&
          requestedEndDateValue &&
          requestedStartDateValue.isAfter(requestedEndDateValue)))
    ) {
      setRequestedStartDateValue(null);
      setRequestedEndDateValue(null);
    }
  };

  const handleDynamicFilterChange = (
    filterKey: string,
    value: string | dayjs.Dayjs | null,
    type: string
  ) => {
    if (type === "checkbox") {
      const currentValue = dynamicFilterValue[filterKey];

      if (!currentValue) {
        setDynamicFilterValue((prev: any) => ({ ...prev, [filterKey]: [value] }));
      } else if (Array.isArray(currentValue)) {
        if (currentValue.includes(value)) {
          if (currentValue.filter((item) => item !== value).length === 0) {
            delete dynamicFilterValue[filterKey];
            setDynamicFilterValue({ ...dynamicFilterValue });
          } else {
            setDynamicFilterValue((prev) => ({
              ...prev,
              [filterKey]: currentValue.filter((item) => item !== value),
            }));
          }
        } else {
          setDynamicFilterValue((prev: any) => ({
            ...prev,
            [filterKey]: [...currentValue, value],
          }));
        }
      }
    } else if (type === "date") {
      setDynamicFilterValue((prev) => ({ ...prev, [filterKey]: value }));
    } else {
      setDynamicFilterValue((prev) => ({ ...prev, [filterKey]: value }));
    }
  };

  return (
    <div className="flex justify-end">
      <div className="relative" ref={filterRef}>
        <ButtonWithImg
          buttonName={translate("Filter")}
          ImgSrc={"filter"}
          className="px-4"
          buttonOnclick={handleClickFilterOpen}
          testId={"filter"}
        />
        {isFilter && (
          <div
            className={`py-[16px] px-4 bg-white border border-s_dark_hover shadow-lg rounded-lg absolute ${notEnglish ? "left-0" : "right-0"
              } mt-2 z-[60]`}
          >
            <div className="w-[588px]">
              <p className="text-heading4 text-primary_text mb-3">
                {translate("Filter By")}
              </p>
              <hr className="2xl:mb-4 mb-2 border-neutral-300" />
              <div className="flex">
                <div className={`${notEnglish ? "border-l ml-4" : "border-r"}`}>
                  {isServiceFeeList ? (
                    <>
                      <p
                        data-testid="processed_date"
                        className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "processed_date"
                          ? "text-primary_text"
                          : "text-secondary_text"
                          }`}
                        onClick={() => handleFilterActive("processed_date")}
                      >
                        {translate("Service Fee Processed Date")}
                      </p>
                      <p
                        data-testid="received_date"
                        className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "received_date"
                          ? "text-primary_text"
                          : "text-secondary_text"
                          }`}
                        onClick={() => handleFilterActive("received_date")}
                      >
                        {translate("Service Fee Received Date")}
                      </p>
                    </>
                  ) : (
                    <>
                      {!mainEndPoint?.includes('requests') && (
                        <p
                          data-testid="date_range"
                          className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "date"
                            ? "text-primary_text"
                            : "text-secondary_text"
                            }`}
                          onClick={() => handleFilterActive("date")}
                        >
                          {!amountList
                            ? translate("Date Range")
                            : translate("Processed Date")}
                        </p>
                      )}
                      {amountList && activeList === "Purchased" && (
                        <>
                          <p
                            data-testid="date_range"
                            className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "purchased_date"
                              ? "text-primary_text"
                              : "text-secondary_text"
                              }`}
                            onClick={() => handleFilterActive("purchased_date")}
                          >
                            {translate("Purchased Date")}
                          </p>
                          <p
                            data-testid="credit_status"
                            className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "credit_status"
                              ? "text-primary_text"
                              : "text-secondary_text"
                              }`}
                            onClick={() => handleFilterActive("credit_status")}
                          >
                            {translate("Credit Status")}
                          </p>
                        </>
                      )}
                      {!mnoList && !amountList && !statusNotFilter && (
                        <p
                          className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "status"
                            ? "text-primary_text"
                            : "text-secondary_text"
                            }`}
                          data-testid="status"
                          onClick={() => handleFilterActive("status")}
                        >
                          {translate("Status")}
                        </p>
                      )}
                      {serviceNow && (
                        <p
                          className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "serviceNow"
                            ? "text-primary_text"
                            : "text-secondary_text"
                            }`}
                          data-testid="service_fee_type"
                          onClick={() => handleFilterActive("serviceNow")}
                        >
                          Service Fee Type
                        </p>
                      )}
                      {mainEndPoint?.includes('customers') && (
                        <p
                          className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "Customer Type"
                            ? "text-primary_text"
                            : "text-secondary_text"
                            }`}
                          data-testid="customer_type"
                          onClick={() => handleFilterActive("Customer Type")}
                        >
                          Customer Type
                        </p>
                      )}
                    </>
                  )}
                  {dynamicFilter && (
                    <DynamicFilter
                      activeFilter={activeFilter}
                      dynamicFilter={dynamicFilter}
                      leftSide={true}
                      onFilterChange={handleFilterActive}
                      startDate={requestedStartDateValue}
                      endDate={requestedEndDateValue}
                      handleDateChange={handleDateChange}
                      errorMessage={errorMessage}
                    />
                  )}
                  {location?.pathname?.includes('/onboarding-request/modification') && (
                    <>
                      <p
                        data-testid="edited_with_compliance"
                        className={`pb-3 pr-3 cursor-pointer text-title1 ${activeFilter === "Edited with Compliance"
                          ? "text-primary_text"
                          : "text-secondary_text"
                          }`}
                        onClick={() => handleFilterActive("Edited with Compliance")}
                      >
                        {translate("Edited with Compliance")}
                      </p>
                    </>
                  )}
                </div>
                <div
                  className="pl-4 max-w-[394px] w-full"
                  style={{ minHeight: "100px" }}
                >
                  {activeFilter === "date" && (
                    <div
                      className="flex justify-between gap-3"
                      onMouseDown={preventCloseOnDatePickerClick}
                    >
                      <CustomDatePicker
                        testID="start-date"
                        label={translate("Start Date")}
                        handleStates={handleDateChange}
                        value={startDate}
                        error={
                          errorMessage === "Please select start date"
                            ? "Please select start date"
                            : ""
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="start"
                        required
                        classType={"filter"}
                      />
                      <CustomDatePicker
                        testID="end-date"
                        label={translate("End Date")}
                        handleStates={handleDateChange}
                        value={endDate}
                        required
                        error={
                          errorMessage === "Please select end date"
                            ? "Please select end date"
                            : ""
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="end"
                        classType={"filter"}
                      />
                    </div>
                  )}
                  {activeFilter === "purchased_date" &&
                    activeList === "Purchased" && (
                      <div
                        className="flex justify-between gap-3"
                        onMouseDown={preventCloseOnDatePickerClick}
                      >
                        <CustomDatePicker
                          testID="start-date"
                          label={translate("Start Date")}
                          handleStates={handleDateChange}
                          value={purchasedStartDate}
                          error={
                            errorMessage === "Please select start date"
                              ? "Please select start date"
                              : ""
                          }
                          disabled={false}
                          fieldError={!!errorMessage}
                          type="purchased_start"
                          required
                          classType={"filter"}
                        />
                        <CustomDatePicker
                          testID="end-date"
                          label={translate("End Date")}
                          handleStates={handleDateChange}
                          value={purchasedEndDate}
                          required
                          error={
                            errorMessage === "Please select end date"
                              ? "Please select end date"
                              : ""
                          }
                          disabled={false}
                          fieldError={!!errorMessage}
                          type="purchased_end"
                          classType={"filter"}
                        />
                      </div>
                    )}
                  {activeFilter === "processed_date" && (
                    <div
                      className="flex justify-between gap-3"
                      onMouseDown={preventCloseOnDatePickerClick}
                    >
                      <CustomDatePicker
                        testID="processed-start-date"
                        label={translate("Start Date")}
                        handleStates={handleDateChange}
                        value={processedStartDate}
                        error={
                          errorMessage === "Please select start date"
                            ? "Please select start date"
                            : ""
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="processed_start"
                        required
                        classType={"filter"}
                      />
                      <CustomDatePicker
                        testID="processed-end-date"
                        label={translate("End Date")}
                        handleStates={handleDateChange}
                        value={processedEndDate}
                        required
                        error={
                          errorMessage === "Please select end date"
                            ? "Please select end date"
                            : ""
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="processed_end"
                        classType={"filter"}
                      />
                    </div>
                  )}
                  {activeFilter === "received_date" && (
                    <div
                      className="flex justify-between gap-3"
                      onMouseDown={preventCloseOnDatePickerClick}
                    >
                      <CustomDatePicker
                        testID="received-start-date"
                        label={translate("Start Date")}
                        handleStates={handleDateChange}
                        value={receivedStartDate}
                        error={
                          errorMessage === "Please select start date"
                            ? "Please select start date"
                            : ""
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="received_start"
                        required
                        classType={"filter"}
                      />
                      <CustomDatePicker
                        testID="received-end-date"
                        label={translate("End Date")}
                        handleStates={handleDateChange}
                        value={receivedEndDate}
                        required
                        error={
                          errorMessage === "Please select end date"
                            ? "Please select end date"
                            : ""
                        }
                        disabled={false}
                        fieldError={!!errorMessage}
                        type="received_end"
                        classType={"filter"}
                      />
                    </div>
                  )}
                  {activeFilter === "credit_status" &&
                    activeList === "Purchased" && (
                      <div>
                        <CustomRadioButton
                          options={[
                            { id: "all", label: translate("All") },
                            { id: "true", label: translate("Received") },
                            { id: "false", label: translate("Pending") },
                          ]}
                          selected={creditStatusValue}
                          onChange={handleCreditStatus}
                        />
                      </div>
                    )}
                  {dynamicFilter && (
                    <DynamicFilter
                      activeFilter={activeFilter}
                      dynamicFilter={dynamicFilter}
                      rightSide={true}
                      onFilterChange={handleFilterActive}
                      onDynamicFilterChange={handleDynamicFilterChange}
                      dynamicFilterValue={dynamicFilterValue}
                      startDate={requestedStartDateValue}
                      endDate={requestedEndDateValue}
                      handleDateChange={handleDateChange}
                      errorMessage={errorMessage}
                    />
                  )}
                  {activeFilter === "Edited with Compliance" && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: translate("All") },
                          { id: "true", label: translate("Yes") },
                          { id: "false", label: translate("No") },
                        ]}
                        selected={editedWithCompliance}
                        onChange={handleEditedWithCompliance}
                      />
                    </div>
                  )}
                  {activeFilter === "status" && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: translate("All") },
                          { id: "active", label: translate("Active") },
                          { id: "inactive", label: translate("Inactive") },
                        ]}
                        selected={selectedOption}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                  {activeFilter === "serviceNow" && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: "All" },
                          { id: "Upfront", label: "Upfront" },
                          { id: "Postpaid", label: "Postpaid" },
                        ]}
                        selected={serviceFee}
                        onChange={handleServiceFee}
                      />
                    </div>
                  )}
                  {activeFilter === "Customer Type" && (
                    <div>
                      <CustomRadioButton
                        options={[
                          { id: "all", label: "All" },
                          { id: "Individual", label: "Individual" },
                          { id: "Corporate", label: "Corporate" },
                        ]}
                        selected={customerType}
                        onChange={handleCustomerType}
                      />
                    </div>
                  )}
                  {errorMessage !== "Please select start date" &&
                    errorMessage !== "Please select end date" &&
                    errorMessage && (
                      <div className="pt-1">
                        <ErrorMessage errorMessage={errorMessage} />
                      </div>
                    )}
                </div>
              </div>
              <div className="gap-3 flex pt-6">
                <Button
                  buttonName={translate("Clear All")}
                  textSize="text-large"
                  buttonHight="h-10"
                  testId="clear_all"
                  textColor={ClearButtonDisable ? "" : "text-p_primary"}
                  className={`px-4 ${ClearButtonDisable ? "" : "bg-white border border-p_primary"
                    } `}
                  buttonOnclick={handleClearAll}
                  buttonDisabled={ClearButtonDisable}
                />
                <Button
                  buttonName={translate("Apply")}
                  textSize="text-large"
                  buttonHight="h-10"
                  testId="apply_filter"
                  className={`px-4 bg-p_primary text-white ${notEnglish ? "w-fit" : "w-24"
                    }`}
                  buttonOnclick={handleApply}
                  buttonDisabled={!!errorMessage}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Filter;
